/*
 * Sticky add to cart bar, mobile only.
 *
 * The bar carries no form of its own. Its button proxies a click into the
 * exact main product form named by the snippet. Variant, quantity and the BSS
 * B2B pricing app therefore continue to operate on one form.
 *
 * Price, availability, loading state and errors are mirrored from the live
 * DOM so the bar follows both the theme's variant re-render and client-side
 * B2B price rewrites.
 */

(function () {
  'use strict';

  var BAR_SELECTOR = '[data-mb-sticky-atc]';
  var controllers = new WeakMap();

  if (window.MBStickyATC && window.MBStickyATC.init) {
    window.MBStickyATC.init(document);
    return;
  }

  function elementsWithin(root) {
    var bars = [];
    if (root.matches && root.matches(BAR_SELECTOR)) bars.push(root);
    if (root.querySelectorAll) {
      bars = bars.concat(Array.prototype.slice.call(root.querySelectorAll(BAR_SELECTOR)));
    }
    return bars;
  }

  function overlayOpen() {
    var classes = document.body.classList;
    return (
      classes.contains('overflow-hidden') ||
      classes.contains('overflow-hidden-mobile') ||
      classes.contains('overflow-hidden-tablet') ||
      !!document.querySelector('#cart-notification.active')
    );
  }

  function refreshDocumentSpacing() {
    var visibleBar = document.querySelector(BAR_SELECTOR + '.is-visible');
    document.documentElement.classList.toggle('mb-sticky-atc-visible', !!visibleBar);

    if (visibleBar) {
      document.documentElement.style.setProperty(
        '--mb-sticky-atc-height',
        visibleBar.getBoundingClientRect().height + 'px'
      );
    } else {
      document.documentElement.style.removeProperty('--mb-sticky-atc-height');
    }
  }

  function initBar(bar) {
    if (!bar || controllers.has(bar)) return;

    var formId = bar.dataset.mbProductFormId;
    var form = formId ? document.getElementById(formId) : null;
    var productForm = form ? form.closest('product-form') : null;
    var realBtn = form ? form.querySelector('.product-form__submit') : null;
    var stickyBtn = bar.querySelector('[data-mb-sticky-btn]');
    var stickyPrice = bar.querySelector('[data-mb-sticky-price]');
    var stickyStatus = bar.querySelector('[data-mb-sticky-status]');

    // A product template can omit the buy-buttons block. Keep the component
    // inert instead of deleting markup or accidentally binding another form.
    if (!form || !productForm || !realBtn || !stickyBtn || !stickyPrice) {
      bar.hidden = true;
      return;
    }

    var priceHost = document.getElementById('price-' + bar.dataset.sectionId);
    var errorWrapper = productForm.querySelector('.product-form__error-message-wrapper');
    var footer = document.querySelector('.footer, #shopify-section-footer');
    var cartNotification = document.getElementById('cart-notification');
    var observers = [];
    var ticking = false;
    var destroyed = false;

    function realBtnLabel() {
      var span = realBtn.querySelector('span');
      return ((span || realBtn).textContent || '').trim();
    }

    function readPrice() {
      if (!priceHost) return null;

      // price.liquid renders regular and sale containers together. Read the
      // one the theme marks visible so BSS's rewritten customer price wins.
      var priceEl = priceHost.querySelector('.price');
      var onSale = !!priceEl && priceEl.classList.contains('price--on-sale');
      var node = onSale
        ? priceHost.querySelector('.price__sale .price-item--sale')
        : priceHost.querySelector('.price__regular .price-item--regular');

      if (!node) {
        node =
          priceHost.querySelector('.price-item--sale') ||
          priceHost.querySelector('.price-item--regular');
      }
      if (!node) return null;

      var text = (node.textContent || '').replace(/\s+/g, ' ').trim();
      return text || null;
    }

    function realButtonLoading() {
      return (
        realBtn.getAttribute('aria-disabled') === 'true' ||
        realBtn.classList.contains('loading')
      );
    }

    function realButtonBlocked() {
      return realBtn.disabled || realButtonLoading();
    }

    function syncError() {
      if (!stickyStatus || !errorWrapper) return;
      var message = (errorWrapper.textContent || '').replace(/\s+/g, ' ').trim();
      stickyStatus.textContent = !errorWrapper.hasAttribute('hidden') && message ? message : '';
    }

    function syncState() {
      var loading = realButtonLoading();
      stickyBtn.disabled = realButtonBlocked();
      if (loading) stickyBtn.setAttribute('aria-busy', 'true');
      else stickyBtn.removeAttribute('aria-busy');

      var label = realBtnLabel();
      if (label) stickyBtn.textContent = label;

      var price = readPrice();
      if (price) stickyPrice.textContent = price;
      syncError();
    }

    function update() {
      if (destroyed) return;

      var buttonRect = realBtn.getBoundingClientRect();
      var viewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;
      var footerInView = false;

      if (footer) {
        var footerRect = footer.getBoundingClientRect();
        footerInView = footerRect.top < viewportHeight && footerRect.bottom > 0;
      }

      // Do not expose the shortcut before the shopper has reached the real
      // variant, quantity and add controls. It appears only after they scroll
      // past the real button.
      var isOverlayOpen = overlayOpen();
      var shouldShow = buttonRect.bottom < 0 && !footerInView && !isOverlayOpen;

      // Keep a keyboard-focused shortcut visible until focus leaves it. An
      // opening modal or cart dialog still wins and manages focus itself.
      if (!isOverlayOpen && bar.contains(document.activeElement)) shouldShow = true;

      bar.classList.toggle('is-visible', shouldShow);
      refreshDocumentSpacing();
      ticking = false;
    }

    function scheduleUpdate() {
      if (ticking || destroyed) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    function onClick() {
      if (realButtonBlocked()) return;
      realBtn.click();
      syncState();
    }

    function observe(target, options, callback) {
      if (!target) return;
      var observer = new MutationObserver(callback);
      observer.observe(target, options);
      observers.push(observer);
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      stickyBtn.removeEventListener('click', onClick);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', scheduleUpdate);
      }
      observers.forEach(function (observer) {
        observer.disconnect();
      });
      bar.classList.remove('is-visible');
      controllers.delete(bar);
      refreshDocumentSpacing();
    }

    stickyBtn.addEventListener('click', onClick);
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleUpdate, { passive: true });
    }

    observe(
      priceHost,
      {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true,
        characterData: true,
      },
      syncState
    );
    observe(
      realBtn,
      {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
        characterData: true,
      },
      syncState
    );
    observe(
      errorWrapper,
      {
        attributes: true,
        attributeFilter: ['hidden'],
        childList: true,
        subtree: true,
        characterData: true,
      },
      syncError
    );
    observe(document.body, { attributes: true, attributeFilter: ['class'] }, scheduleUpdate);
    observe(cartNotification, { attributes: true, attributeFilter: ['class'] }, scheduleUpdate);

    controllers.set(bar, { destroy: destroy, update: update, syncState: syncState });
    syncState();
    update();
  }

  function init(root) {
    elementsWithin(root || document).forEach(initBar);
  }

  function destroyWithin(root) {
    elementsWithin(root || document).forEach(function (bar) {
      var controller = controllers.get(bar);
      if (controller) controller.destroy();
    });
  }

  window.MBStickyATC = {
    init: init,
    destroy: destroyWithin,
  };

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyWithin(event.target);
  });

  init(document);
})();
